// src/components/PendingEnrollmentsPanel.js
//
// Admin-only "Pending approval" tab on /admin/study-patients. Lists the
// enrollments coordinators recorded on patient pages, across every study at
// this site. The admin approves each onto the study's enrolled (master) list
// with its patient number, links it to a row already on the list, or rejects
// it. The master list changes only here, only on Approve.

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  ExternalLink,
  AlertTriangle,
  Link2,
  RefreshCw,
  Inbox,
} from "lucide-react";
import {
  fetchPendingEnrollmentRequests,
  fetchUnsyncedApprovals,
  findExistingMasterListRows,
  findListMatchesForRequests,
  approveEnrollmentRequest,
  rejectEnrollmentRequest,
  retryPatientSync,
} from "../services/enrollmentRequestService";
import { fetchStudies } from "../services/studyService";
import { isClosedStudy } from "../services/enrollmentRules";

const inputCls =
  "w-full rounded-lg bg-white/[0.04] border border-white/10 px-2.5 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50";

const patientLink = (r) =>
  `/coordinator/patient/${encodeURIComponent(r.patientId)}?src=${r.source === "sheet" ? "sheet" : r.source || "firestore"}`;

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");

const RequestCard = ({ request, studyClosed, listMatches, onDone }) => {
  const [mode, setMode] = useState(null); // "approve" | "reject"
  const [name, setName] = useState(request.patientName || "");
  const [dob, setDob] = useState(request.dob || "");
  const [patientNumber, setPatientNumber] = useState("");
  const [reason, setReason] = useState("");
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const openApprove = async () => {
    setMode("approve");
    setError("");
    try {
      setExisting(await findExistingMasterListRows(request));
    } catch (err) {
      console.error(err);
      setExisting([]);
    }
  };

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      const res = await fn();
      if (!res.ok) setError(res.error || "Couldn't complete this.");
      else onDone(res);
    } catch (err) {
      console.error(err);
      setError("Couldn't complete this. Nothing further was changed; try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white">
            {request.patientName || "Unnamed patient"}
            <span className="ml-2 text-[11px] font-normal text-slate-400">DOB {request.dob || "—"}</span>
          </p>
          <p className="text-xs text-emerald-200 mt-0.5">
            Enrolled in {request.studyName || request.studyId}
            {studyClosed && (
              <span className="ml-2 inline-flex items-center rounded-md border border-slate-400/30 bg-white/[0.05] px-1.5 py-0.5 text-[10px] font-semibold text-slate-300">
                STUDY CLOSED — approving still records that they were enrolled
              </span>
            )}
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Recorded by {request.requestedBy} on {fmt(request.requestedAt)}
            {request.source === "sheet" ? " · Google Sheet patient" : ""}
          </p>
        </div>
        <Link
          to={patientLink(request)}
          className="inline-flex items-center gap-1 text-[11px] text-purple-300 hover:text-white"
        >
          Open patient <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      {/* Up-front warning, before anyone clicks Approve. Linking is the same
          write as the Approve panel's "Same patient" button: nothing is added
          to the list. Hidden while Approve is open, which lists them itself. */}
      {mode !== "approve" && listMatches && listMatches.length > 0 && (
        <div className="enroll-maybe-listed mt-2 rounded-lg border p-2 text-[11px]">
          <p className="flex items-center gap-1 font-semibold">
            <AlertTriangle className="w-3 h-3" /> Might already be on the enrolled list
          </p>
          <ul className="mt-1 space-y-1">
            {listMatches.map((m) => (
              <li key={m.row.id} className="flex flex-wrap items-center gap-2">
                <span>
                  {m.row.NAME || m.row.name} · DOB {m.row.DOB || m.row.dob} · #{m.row.PATIENT_NUMBER || "—"} ·{" "}
                  {m.score}% match
                  {/* Why it isn't 100%, e.g. "Only one name on both records". */}
                  {m.score < 100 && m.reasons && m.reasons.length > 0 && (
                    <span className="enroll-maybe-listed-why"> ({m.reasons.join("; ")})</span>
                  )}
                </span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (
                      !window.confirm(
                        `Link and approve?\n\n${request.patientName} will be approved for ${
                          request.studyName || request.studyId
                        } as the patient already on the enrolled list (${m.row.NAME || m.row.name}). Nothing is added to the list.`
                      )
                    )
                      return;
                    run(() =>
                      approveEnrollmentRequest(request, {
                        name: request.patientName || "",
                        dob: request.dob || "",
                        linkRowId: m.row.id,
                      })
                    );
                  }}
                  className="enroll-maybe-listed-btn inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-semibold disabled:opacity-40"
                >
                  <Link2 className="w-3 h-3" /> Same patient — link to this record
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!mode && (
        <div className="mt-2 flex gap-1.5">
          <button
            type="button"
            onClick={openApprove}
            className="inline-flex items-center gap-1 rounded-lg border border-emerald-300/40 bg-emerald-500/20 px-2.5 py-1 text-[11px] font-semibold text-emerald-100 hover:bg-emerald-500/30"
          >
            <CheckCircle2 className="w-3 h-3" /> Approve
          </button>
          <button
            type="button"
            onClick={() => setMode("reject")}
            className="inline-flex items-center gap-1 rounded-lg border border-white/15 bg-white/[0.05] px-2.5 py-1 text-[11px] font-semibold text-slate-300 hover:bg-white/[0.1]"
          >
            <XCircle className="w-3 h-3" /> Reject
          </button>
        </div>
      )}

      {mode === "approve" && (
        <div className="mt-3 space-y-2">
          {existing === null && (
            <p className="flex items-center gap-1.5 text-[11px] text-slate-400">
              <Loader2 className="w-3 h-3 animate-spin" /> Checking the study's enrolled list…
            </p>
          )}
          {existing && existing.length > 0 && (
            <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 p-2 text-[11px] text-amber-100">
              <p className="flex items-center gap-1 font-semibold">
                <AlertTriangle className="w-3 h-3" /> This patient may already be on the enrolled list:
              </p>
              <ul className="mt-1 space-y-1">
                {existing.map((m) => (
                  <li key={m.row.id} className="flex flex-wrap items-center gap-2">
                    <span>
                      {m.row.NAME || m.row.name} · DOB {m.row.DOB || m.row.dob} · #{m.row.PATIENT_NUMBER || "—"} ·{" "}
                      {m.score}% match
                    </span>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => run(() => approveEnrollmentRequest(request, { name, dob, linkRowId: m.row.id }))}
                      className="inline-flex items-center gap-1 rounded-md border border-amber-300/40 px-2 py-0.5 font-semibold hover:bg-amber-400/20"
                    >
                      <Link2 className="w-3 h-3" /> Same patient — approve without adding
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {existing !== null && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <label className="text-[10px] uppercase tracking-wider text-slate-400">
                  Name
                  <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                <label className="text-[10px] uppercase tracking-wider text-slate-400">
                  Date of birth
                  <input type="date" className={inputCls} value={dob} onChange={(e) => setDob(e.target.value)} />
                </label>
                <label className="text-[10px] uppercase tracking-wider text-slate-400">
                  Patient number <span className="text-rose-400">*</span>
                  <input
                    className={inputCls}
                    value={patientNumber}
                    onChange={(e) => setPatientNumber(e.target.value)}
                    placeholder="Study patient #"
                  />
                </label>
              </div>
              <p className="text-[10px] text-slate-500">
                Name and date of birth come from the patient's record, so the enrolled list matches it.
              </p>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  disabled={busy || !patientNumber.trim() || !name.trim() || !dob}
                  onClick={() =>
                    run(() =>
                      approveEnrollmentRequest(request, {
                        name: name.trim(),
                        dob,
                        patientNumber: patientNumber.trim(),
                      })
                    )
                  }
                  className="enroll-solid-btn inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 px-3 py-1.5 text-[11px] font-semibold text-white disabled:opacity-40"
                >
                  {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                  Add to enrolled list
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setMode(null)}
                  className="rounded-lg border border-white/15 px-3 py-1.5 text-[11px] text-slate-300"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {mode === "reject" && (
        <div className="mt-3 space-y-2">
          <input
            className={inputCls}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why? (e.g. not enrolled in this study, wrong study picked)"
          />
          <p className="text-[10px] text-slate-500">
            The enrolled list isn't changed. The patient's record keeps this study marked as rejected.
          </p>
          <div className="flex gap-1.5">
            <button
              type="button"
              disabled={busy || !reason.trim()}
              onClick={() => run(() => rejectEnrollmentRequest(request, reason.trim()))}
              className="enroll-solid-btn inline-flex items-center gap-1 rounded-lg bg-rose-500/80 px-3 py-1.5 text-[11px] font-semibold text-white disabled:opacity-40"
            >
              {busy && <Loader2 className="w-3 h-3 animate-spin" />}
              Reject
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setMode(null)}
              className="rounded-lg border border-white/15 px-3 py-1.5 text-[11px] text-slate-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <p className="mt-2 text-[11px] text-rose-300">{error}</p>}
    </div>
  );
};

const PendingEnrollmentsPanel = ({ onCountChange }) => {
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [unsynced, setUnsynced] = useState([]);
  const [closedStudyIds, setClosedStudyIds] = useState(new Set());
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");
  // { [requestId]: matches[] } from the up-front enrolled-list check.
  const [listMatches, setListMatches] = useState({});
  const checkSeq = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    setListMatches({});
    try {
      const [p, u, studies] = await Promise.all([
        fetchPendingEnrollmentRequests(),
        fetchUnsyncedApprovals(),
        fetchStudies(),
      ]);
      setPending(p);
      setUnsynced(u);
      setClosedStudyIds(new Set(studies.filter((s) => isClosedStudy(s)).map((s) => String(s.id))));
      onCountChange?.(p.length);
      // After the cards show, so a slow list never holds up the tab.
      // A Refresh started meanwhile wins over this older check.
      const seq = ++checkSeq.current;
      findListMatchesForRequests(p)
        .then((m) => {
          if (seq === checkSeq.current) setListMatches(m);
        })
        .catch((err) => console.error("Couldn't check the enrolled lists", err));
    } catch (err) {
      console.error("Couldn't load pending enrollments", err);
      setLoadError("Couldn't load pending enrollments.");
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-3">
      <div className="flex items-center gap-2 mb-3">
        <Inbox className="w-3.5 h-3.5 text-purple-300" />
        <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white">Pending approval</h2>
        <span className="text-[10px] text-slate-400">{pending.length}</span>
        <button
          type="button"
          onClick={load}
          className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-white"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {notice && <p className="mb-2 text-[11px] text-emerald-200">{notice}</p>}
      {loading && (
        <p className="flex items-center gap-2 py-6 justify-center text-xs text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </p>
      )}
      {loadError && <p className="py-4 text-xs text-rose-300">{loadError}</p>}

      {!loading && unsynced.length > 0 && (
        <div className="mb-3 rounded-xl border border-amber-400/30 bg-amber-400/10 p-2.5 text-[11px] text-amber-100">
          <p className="font-semibold mb-1">Decided, but the patient's record wasn't updated:</p>
          <ul className="space-y-1">
            {unsynced.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2">
                {r.patientName} · {r.studyName} ({r.status}){r.patientSyncError ? ` — ${r.patientSyncError}` : ""}
                <button
                  type="button"
                  onClick={async () => {
                    const res = await retryPatientSync(r);
                    setNotice(res.ok ? `Updated ${r.patientName}'s record.` : `Still couldn't update: ${res.error}`);
                    load();
                  }}
                  className="rounded-md border border-amber-300/40 px-2 py-0.5 font-semibold hover:bg-amber-400/20"
                >
                  Retry
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!loading && !loadError && pending.length === 0 && (
        <p className="py-6 text-center text-xs text-slate-400">Nothing waiting for approval.</p>
      )}

      <div className="space-y-2">
        {pending.map((r) => (
          <RequestCard
            key={r.id}
            request={r}
            studyClosed={closedStudyIds.has(String(r.studyId))}
            listMatches={listMatches[r.id]}
            onDone={(res) => {
              setNotice(
                res.patientSynced === false
                  ? `Done for ${r.patientName}, but their patient record couldn't be updated. Use Retry above.`
                  : `Done: ${r.patientName} · ${r.studyName}.`
              );
              load();
            }}
          />
        ))}
      </div>
    </section>
  );
};

export default PendingEnrollmentsPanel;
